import { useEffect, useState } from 'react';
import { voice } from '@repletoryreach/sdk-api';
import { Stack, Card, Group, Text, Button, SimpleGrid, Badge } from '@repletoryreach/sdk-ui';
import { Alert, DataTable, EmptyState } from '@repletoryreach/sdk-ui/data-display';
import { Modal } from '@repletoryreach/sdk-ui/overlays';
import { NumberInput, Switch, Textarea, TextInput, Select, PasswordInput } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { TenantName } from '../../components/TenantName';
import { formatDateTime, formatMinor } from '../../lib/format';
import type { Indexed } from '../../lib/types';
import {
  useVoiceApplications, useVoiceCarriers, useVoiceInventory, useApproveVoiceApplication, useRejectVoiceApplication,
  useSaveVoiceCarrier, useDeleteVoiceCarrier, useAddVoiceInventory, useUpdateVoiceInventory
} from '../../data/useVoice';
import { useT } from '../../i18n/I18nProvider';

const MUTED = 'var(--rr-color-text-muted)';
const fail = (e: unknown, fallback: string) => notifyError(e instanceof Error ? e.message : fallback);

/**
 * The numbers the platform supplies: who has asked for one, the carriers they
 * arrive on, and the numbers held. Approving takes the fee the applicant has
 * had held since applying and assigns a number in one step; rejecting gives
 * the fee back and tells them why.
 */
export function Supply() {
  return (
    <>
      <ApplicationsCard />
      <CarriersCard />
      <InventoryCard />
    </>
  );
}

function ApplicationsCard() {
  async function openDocument(applicationId: string, mediaId: string) {
    try {
      const link = await voice.adminOpenApplicationDocument(applicationId, mediaId);
      window.open(link.data.url, '_blank', 'noopener');
    } catch (e) {
      notifyError(e instanceof Error ? e.message : t('voice.toast.failed'));
    }
  }

  const t = useT();
  const applications = useVoiceApplications();
  const inventory = useVoiceInventory();
  const approve = useApproveVoiceApplication();
  const reject = useRejectVoiceApplication();
  const [choice, setChoice] = useState<Record<string, string>>({});
  const [rejecting, setRejecting] = useState<voice.VoiceApplication | null>(null);
  const [reason, setReason] = useState('');
  const rows = (applications.data?.data ?? []) as Indexed<voice.VoiceApplication>[];
  const available = (inventory.data?.data ?? []).filter((i) => i.status === 'available');

  const doApprove = async (a: voice.VoiceApplication) => {
    const inventoryId = choice[a.id];
    if (!inventoryId) return;
    try { const res = await approve.mutateAsync({ id: a.id, inventoryId }); notifySuccess(t('voice.supply.applications.approved', { number: res.data.number ?? '' })); }
    catch (e) { fail(e, t('voice.loadFailed')); }
  };
  const doReject = async () => {
    if (!rejecting) return;
    try { await reject.mutateAsync({ id: rejecting.id, reason }); notifySuccess(t('voice.supply.applications.rejected')); setRejecting(null); setReason(''); }
    catch (e) { fail(e, t('voice.loadFailed')); }
  };

  return (
    <Card withBorder data-testid="voice-applications">
      <Stack gap="md">
        <Stack gap="xs">
          <Text role="label">{t('voice.supply.applications.title')}</Text>
          <Text role="bodySmall" c={MUTED}>{t('voice.supply.applications.hint')}</Text>
        </Stack>
        {applications.isError && <Alert color="error" role="alert">{t('voice.loadFailed')}</Alert>}
        {!applications.isPending && rows.length === 0 && <EmptyState title={t('voice.supply.applications.empty')} description={t('voice.supply.applications.emptyHint')} />}
        {rows.length > 0 && (
          <DataTable
            records={rows}
            idAccessor="id"
            columns={[
              {
                accessor: 'organisation_id', title: t('voice.tenants.organisation'),
                render: (r) => (
                  <Stack gap="xs">
                    <TenantName id={r.organisation_id} />
                    <Text role="caption" c={MUTED}>{r.details.business_name}{r.details.registration_number ? ` · ${r.details.registration_number}` : ''}</Text>
                  </Stack>
                )
              },
              {
                accessor: 'details', title: t('voice.supply.applications.contact'),
                render: (r) => (
                  <Stack gap="xs">
                    <Text role="bodySmall">{r.details.contact_name} · {r.details.contact_phone}</Text>
                    <Text role="caption" c={MUTED}>{r.details.purpose}</Text>
                    {/* The RICA documents: each opens through a five-minute link, and each opening is recorded. */}
                    {(r.documents ?? []).map((d) => (
                      <Group key={d.media_id} gap="xs" wrap="nowrap">
                        <Text role="caption">{d.requirement ?? d.name ?? t('voice.supply.applications.document')}</Text>
                        <Button size="xs" variant="subtle" onClick={() => void openDocument(r.id, d.media_id)}>{t('voice.supply.applications.open')}</Button>
                      </Group>
                    ))}
                  </Stack>
                )
              },
              { accessor: 'number_kind', title: t('voice.supply.applications.kind'), render: (r) => t(`voice.supply.kind.${r.number_kind}`) },
              { accessor: 'fee_minor', title: t('voice.supply.applications.fee'), render: (r) => formatMinor(r.fee_minor, 'ZAR') },
              {
                accessor: 'status', title: t('voice.tenants.status'),
                render: (r) => (
                  <Stack gap="xs">
                    <Badge variant="light" color={r.status === 'approved' ? 'green' : r.status === 'rejected' ? 'red' : r.status === 'submitted' ? 'yellow' : 'gray'}>{t(`voice.supply.status.${r.status}`)}</Badge>
                    {r.number && <Text role="caption">{r.number}</Text>}
                    {r.reject_reason && <Text role="caption" c={MUTED}>{r.reject_reason}</Text>}
                    <Text role="caption" c={MUTED}>{formatDateTime(r.created_at)}</Text>
                  </Stack>
                )
              },
              {
                accessor: 'actions', title: t('common.actions'),
                render: (r) => (r.status !== 'submitted' ? null : (
                  <Stack gap="xs">
                    <Select
                      aria-label={t('voice.supply.applications.assign')}
                      value={choice[r.id] ?? ''}
                      onChange={(e) => { const picked = e.currentTarget.value; setChoice((c) => ({ ...c, [r.id]: picked })); }}
                      data={[{ value: '', label: t('voice.supply.applications.assign') }, ...available.filter((i) => i.kind === r.number_kind).map((i) => ({ value: i.id, label: i.number }))]}
                    />
                    <Group gap="sm" wrap="nowrap">
                      <Button size="xs" disabled={!choice[r.id]} loading={approve.isPending && approve.variables?.id === r.id} onClick={() => void doApprove(r)}>{t('voice.supply.applications.approve')}</Button>
                      <Button size="xs" variant="default" color="red" onClick={() => setRejecting(r)}>{t('voice.supply.applications.reject')}</Button>
                    </Group>
                  </Stack>
                ))
              }
            ]}
          />
        )}
      </Stack>
      <Modal opened={rejecting !== null} onClose={() => { setRejecting(null); setReason(''); }} title={t('voice.supply.applications.rejectTitle')}>
        <Stack gap="md">
          <Text role="bodySmall">{t('voice.supply.applications.rejectBody')}</Text>
          <Textarea label={t('voice.reason.label')} description={t('voice.supply.applications.rejectHint')} value={reason} minRows={2} onChange={(e) => setReason(e.currentTarget.value)} />
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => { setRejecting(null); setReason(''); }}>{t('action.cancel')}</Button>
            <Button color="red" loading={reject.isPending} disabled={!reason.trim()} onClick={() => void doReject()}>{t('voice.supply.applications.reject')}</Button>
          </Group>
        </Stack>
      </Modal>
    </Card>
  );
}

function CarriersCard() {
  const t = useT();
  const carriers = useVoiceCarriers();
  const save = useSaveVoiceCarrier();
  const remove = useDeleteVoiceCarrier();
  const [editing, setEditing] = useState<voice.VoiceCarrier | 'new' | null>(null);
  const existing = editing !== null && editing !== 'new' ? editing : null;
  const [name, setName] = useState('');
  const [mode, setMode] = useState<voice.VoiceProviderMode>('forward');
  const [host, setHost] = useState('');
  const [port, setPort] = useState<number | ''>(5060);
  const [transport, setTransport] = useState<'udp' | 'tcp' | 'tls'>('udp');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [sources, setSources] = useState('');
  const [limited, setLimited] = useState(false);
  const [active, setActive] = useState(true);

  useEffect(() => {
    if (editing === null) return;
    setName(existing?.name ?? ''); setMode(existing?.mode ?? 'forward'); setHost(existing?.host ?? ''); setPort(existing?.port ?? 5060);
    setTransport(existing?.transport ?? 'udp'); setUsername(existing?.username ?? ''); setPassword('');
    setSources((existing?.allowed_sources ?? []).join('\n')); setLimited(existing?.spend_limited ?? false); setActive(existing?.is_active ?? true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const doSave = async () => {
    try {
      await save.mutateAsync({
        id: existing?.id,
        input: {
          name: name.trim(), mode, host: host.trim(), port: port === '' ? 5060 : port, transport,
          username: mode === 'register' ? username.trim() : undefined, password: mode === 'register' && password ? password : undefined,
          allowed_sources: sources.split(/[\n,]/).map((s) => s.trim()).filter(Boolean), spend_limited: limited, is_active: active
        }
      });
      notifySuccess(t('voice.supply.carriers.saved'));
      setEditing(null);
    } catch (e) { fail(e, t('voice.loadFailed')); }
  };
  const doRemove = async (id: string) => {
    try { await remove.mutateAsync(id); notifySuccess(t('voice.supply.carriers.removed')); }
    catch (e) { fail(e, t('voice.loadFailed')); }
  };

  return (
    <Card withBorder data-testid="voice-carriers">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="wrap">
          <Stack gap="xs">
            <Text role="label">{t('voice.supply.carriers.title')}</Text>
            <Text role="bodySmall" c={MUTED}>{t('voice.supply.carriers.hint')}</Text>
          </Stack>
          <Button size="xs" variant="default" onClick={() => setEditing('new')}>{t('voice.supply.carriers.add')}</Button>
        </Group>
        {!carriers.isPending && (carriers.data?.data ?? []).length === 0 && <EmptyState title={t('voice.supply.carriers.empty')} description={t('voice.supply.carriers.emptyHint')} />}
        {(carriers.data?.data ?? []).map((c) => (
          <Group key={c.id} justify="space-between" className="rr-voice__row" wrap="nowrap">
            <Stack gap="xs" style={{ minWidth: 0 }}>
              <Group gap="sm">
                <Text role="bodySmall" fw={650}>{c.name}</Text>
                <Badge variant="light" color={!c.is_active ? 'gray' : c.status === 'ok' ? 'green' : c.status === 'failing' ? 'red' : 'yellow'}>{t(!c.is_active ? 'voice.supply.carriers.off' : `voice.supply.carriers.status.${c.status}`)}</Badge>
                {!c.spend_limited && <Badge variant="light" color="red">{t('voice.supply.carriers.noLimit')}</Badge>}
              </Group>
              <Text role="caption" c={MUTED}>{t(`voice.supply.carriers.mode.${c.mode}`, { host: c.host })}{c.allowed_sources.length > 0 ? ` · ${c.allowed_sources.join(', ')}` : ''}</Text>
              {c.status === 'failing' && c.last_error && <Text role="caption" c="var(--rr-color-error)">{c.last_error}</Text>}
            </Stack>
            <Group gap="sm" wrap="nowrap">
              <Button size="xs" variant="subtle" onClick={() => setEditing(c)}>{t('voice.supply.edit')}</Button>
              <Button size="xs" variant="subtle" color="red" onClick={() => void doRemove(c.id)}>{t('voice.supply.remove')}</Button>
            </Group>
          </Group>
        ))}
      </Stack>
      <Modal opened={editing !== null} onClose={() => setEditing(null)} title={t(existing ? 'voice.supply.carriers.edit' : 'voice.supply.carriers.add')}>
        <Stack gap="md" data-testid="voice-carrier-form">
          <TextInput label={t('voice.supply.carriers.form.name')} description={t('voice.supply.carriers.form.nameHint')} value={name} maxLength={120} onChange={(e) => setName(e.currentTarget.value)} />
          <Select label={t('voice.supply.carriers.form.mode')} description={t(`voice.supply.carriers.form.modeHint.${mode}`)} value={mode} onChange={(e) => setMode(e.currentTarget.value as voice.VoiceProviderMode)}
            data={[{ value: 'forward', label: t('voice.supply.carriers.form.mode.forward') }, { value: 'register', label: t('voice.supply.carriers.form.mode.register') }]} />
          <TextInput label={t('voice.supply.carriers.form.host')} description={t('voice.supply.carriers.form.hostHint')} value={host} onChange={(e) => setHost(e.currentTarget.value)} />
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <NumberInput label={t('voice.supply.carriers.form.port')} description={t('voice.supply.carriers.form.portHint')} value={port} min={1} max={65535} onChange={(v) => setPort(typeof v === 'number' ? v : '')} />
            <Select label={t('voice.supply.carriers.form.transport')} description={t('voice.supply.carriers.form.transportHint')} value={transport} onChange={(e) => setTransport(e.currentTarget.value as 'udp' | 'tcp' | 'tls')}
              data={[{ value: 'udp', label: 'UDP' /* i18n-ignore: a protocol name */ }, { value: 'tcp', label: 'TCP' /* i18n-ignore: a protocol name */ }, { value: 'tls', label: 'TLS' /* i18n-ignore: a protocol name */ }]} />
          </SimpleGrid>
          {mode === 'register' && (
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              <TextInput label={t('voice.supply.carriers.form.username')} description={t('voice.supply.carriers.form.usernameHint')} value={username} autoComplete="off" onChange={(e) => setUsername(e.currentTarget.value)} />
              <PasswordInput label={t('voice.supply.carriers.form.password')} description={t(existing?.has_password ? 'voice.supply.carriers.form.passwordKeep' : 'voice.supply.carriers.form.passwordHint')} value={password} autoComplete="new-password" onChange={(e) => setPassword(e.currentTarget.value)} />
            </SimpleGrid>
          )}
          <Textarea label={t('voice.supply.carriers.form.sources')} description={t('voice.supply.carriers.form.sourcesHint')} value={sources} minRows={2} onChange={(e) => setSources(e.currentTarget.value)} />
          <Switch label={t('voice.supply.carriers.form.limited')} description={t('voice.supply.carriers.form.limitedHint')} checked={limited} onChange={(v) => setLimited(v)} />
          <Switch label={t('voice.supply.carriers.form.active')} description={t('voice.supply.carriers.form.activeHint')} checked={active} onChange={(v) => setActive(v)} />
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => setEditing(null)}>{t('action.cancel')}</Button>
            <Button loading={save.isPending} disabled={!name.trim() || !host.trim()} onClick={() => void doSave()}>{t('voice.supply.save')}</Button>
          </Group>
        </Stack>
      </Modal>
    </Card>
  );
}

function InventoryCard() {
  const t = useT();
  const inventory = useVoiceInventory();
  const carriers = useVoiceCarriers();
  const add = useAddVoiceInventory();
  const update = useUpdateVoiceInventory();
  const [adding, setAdding] = useState(false);
  const [carrierId, setCarrierId] = useState('');
  const [kind, setKind] = useState<'national' | 'geographic'>('national');
  const [numbers, setNumbers] = useState('');
  const rows = (inventory.data?.data ?? []) as Indexed<voice.VoiceInventoryNumber>[];
  const carrierRows = carriers.data?.data ?? [];
  const carrierName = (id: string) => carrierRows.find((c) => c.id === id)?.name ?? '';

  const doAdd = async () => {
    try {
      const res = await add.mutateAsync({ carrier_id: carrierId, kind, numbers: numbers.split(/[\s,]+/).map((n) => n.trim()).filter(Boolean) });
      notifySuccess(t('voice.supply.inventory.added', { added: res.data.added.length, skipped: res.data.skipped.length }));
      setAdding(false); setNumbers('');
    } catch (e) { fail(e, t('voice.loadFailed')); }
  };
  const retire = async (id: string, retired: boolean) => {
    try { await update.mutateAsync({ id, retired }); }
    catch (e) { fail(e, t('voice.loadFailed')); }
  };

  return (
    <Card withBorder data-testid="voice-inventory">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="wrap">
          <Stack gap="xs">
            <Text role="label">{t('voice.supply.inventory.title')}</Text>
            <Text role="bodySmall" c={MUTED}>{t('voice.supply.inventory.hint')}</Text>
          </Stack>
          <Button size="xs" variant="default" disabled={carrierRows.length === 0} onClick={() => { setCarrierId(carrierRows[0]?.id ?? ''); setAdding(true); }}>{t('voice.supply.inventory.add')}</Button>
        </Group>
        {!inventory.isPending && rows.length === 0 && <EmptyState title={t('voice.supply.inventory.empty')} description={t('voice.supply.inventory.emptyHint')} />}
        {rows.length > 0 && (
          <DataTable
            records={rows}
            idAccessor="id"
            columns={[
              { accessor: 'number', title: t('voice.supply.inventory.number') },
              { accessor: 'kind', title: t('voice.supply.applications.kind'), render: (r) => t(`voice.supply.kind.${r.kind}`) },
              { accessor: 'carrier_id', title: t('voice.supply.inventory.carrier'), render: (r) => carrierName(r.carrier_id) },
              {
                accessor: 'status', title: t('voice.tenants.status'),
                render: (r) => (
                  <Stack gap="xs">
                    <Badge variant="light" color={r.status === 'available' ? 'green' : r.status === 'assigned' ? 'blue' : 'gray'}>{t(`voice.supply.inventory.status.${r.status}`)}</Badge>
                    {r.organisation_id && <TenantName id={r.organisation_id} subtle />}
                  </Stack>
                )
              },
              {
                accessor: 'actions', title: t('common.actions'),
                render: (r) => (r.status === 'assigned' ? null
                  : <Button size="xs" variant="subtle" onClick={() => void retire(r.id, r.status === 'available')}>{t(r.status === 'available' ? 'voice.supply.inventory.retire' : 'voice.supply.inventory.restore')}</Button>)
              }
            ]}
          />
        )}
      </Stack>
      <Modal opened={adding} onClose={() => setAdding(false)} title={t('voice.supply.inventory.add')}>
        <Stack gap="md" data-testid="voice-inventory-form">
          <Select label={t('voice.supply.inventory.carrier')} description={t('voice.supply.inventory.carrierHint')} value={carrierId} onChange={(e) => setCarrierId(e.currentTarget.value)}
            data={carrierRows.map((c) => ({ value: c.id, label: c.name }))} />
          <Select label={t('voice.supply.applications.kind')} description={t('voice.supply.inventory.kindHint')} value={kind} onChange={(e) => setKind(e.currentTarget.value as 'national' | 'geographic')}
            data={[{ value: 'national', label: t('voice.supply.kind.national') }, { value: 'geographic', label: t('voice.supply.kind.geographic') }]} />
          <Textarea label={t('voice.supply.inventory.numbers')} description={t('voice.supply.inventory.numbersHint')} value={numbers} minRows={4} onChange={(e) => setNumbers(e.currentTarget.value)} />
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => setAdding(false)}>{t('action.cancel')}</Button>
            <Button loading={add.isPending} disabled={!carrierId || !numbers.trim()} onClick={() => void doAdd()}>{t('voice.supply.save')}</Button>
          </Group>
        </Stack>
      </Modal>
    </Card>
  );
}
